import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trophy } from "lucide-react";
import ApiService from "../../services/api.service";
import { LiveGameBadge, LiveScorerLine } from "../../components/shared/LiveGameBadge";
import GameStatsEditor from "../../components/shared/GameStatsEditor";
import { localWhenLine } from "../../utils/gameTime";
import "../client/ClientLayout.css";
import "../coach/CoachPages.css";
import "./LeaguePages.css";


function AddGameModal({ teams, onClose, onSaved }) {
  const [teamAId, setTeamAId] = useState("");
  const [teamBId, setTeamBId] = useState("");
  const [gameDate, setGameDate] = useState("");
  const [gameTime, setGameTime] = useState("");
  const [venue, setVenue] = useState("");
  const [address, setAddress] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const canSave = teamAId && teamBId && teamAId !== teamBId && gameDate && gameTime;

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      // timeZone (2026-09-08): gameDate/gameTime are the VENUE wall clock as
      // typed into the two inputs, and this names the zone they belong to.
      // Without it polyp-league treats the pair as the legacy UTC-formatted
      // shape from Scorekeepers <=1.16 and shifts the time.
      await ApiService.createLeagueGame({
        teamAId, teamBId, gameDate, gameTime, venue, address,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      onSaved();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <div className="coach-modal-backdrop" onClick={onClose}>
      <div className="coach-modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="client-h2">Schedule Game</h2>
        <div className="coach-modal-fields">
          <select className="client-input" value={teamAId} onChange={(e) => setTeamAId(e.target.value)}>
            <option value="">Team A</option>
            {teams.map((t) => <option key={t.teamId} value={t.teamId}>{t.name}</option>)}
          </select>
          <select className="client-input" value={teamBId} onChange={(e) => setTeamBId(e.target.value)}>
            <option value="">Team B</option>
            {teams.filter((t) => t.teamId !== teamAId).map((t) => <option key={t.teamId} value={t.teamId}>{t.name}</option>)}
          </select>
          <input className="client-input" type="date" value={gameDate} onChange={(e) => setGameDate(e.target.value)} />
          <input className="client-input" type="time" value={gameTime} onChange={(e) => setGameTime(e.target.value)} />
          <input className="client-input" placeholder="Venue (optional)" value={venue}
            onChange={(e) => setVenue(e.target.value)} />
          <input className="client-input" placeholder="Address (optional)" value={address}
            onChange={(e) => setAddress(e.target.value)} />
        </div>
        {error && <div className="client-error">{error}</div>}
        <div className="coach-modal-actions">
          <button className="client-button-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="client-button" onClick={save} disabled={saving || !canSave}>
            {saving ? "Saving…" : "Schedule Game"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AddTournamentModal({ teams, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [useAllTeams, setUseAllTeams] = useState(true);
  const [selectedTeamIds, setSelectedTeamIds] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [location, setLocation] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const effectiveTeamIds = useAllTeams ? teams.map((t) => t.teamId) : selectedTeamIds;
  let bracketSize = 2;
  while (bracketSize < effectiveTeamIds.length) bracketSize *= 2;
  const byeCount = Math.max(bracketSize - effectiveTeamIds.length, 0);

  const canSave = name.trim() && effectiveTeamIds.length >= 2 && startDate && startTime;

  const toggleTeam = (teamId) => {
    setSelectedTeamIds((prev) => (prev.includes(teamId) ? prev.filter((id) => id !== teamId) : [...prev, teamId]));
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      // Same timeZone rule as createLeagueGame above — startDate/startTime are
      // the venue wall clock, and every game the bracket mints inherits them,
      // so omitting the zone shifts the whole tournament.
      const resp = await ApiService.createLeagueTournament({
        name, teamIds: effectiveTeamIds, startDate, startTime, location,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      onSaved(resp.tournamentId);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <div className="coach-modal-backdrop" onClick={onClose}>
      <div className="coach-modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="client-h2">New Tournament</h2>
        <div className="coach-modal-fields">
          <input className="client-input" placeholder="Tournament name" value={name} onChange={(e) => setName(e.target.value)} />
          <label className="league-all-teams-toggle">
            <input type="checkbox" checked={useAllTeams} onChange={(e) => setUseAllTeams(e.target.checked)} />
            All Teams
          </label>
          {!useAllTeams && (
            <div className="league-team-checklist">
              {teams.map((t) => (
                <label key={t.teamId} className="league-team-checklist-row">
                  <input type="checkbox" checked={selectedTeamIds.includes(t.teamId)} onChange={() => toggleTeam(t.teamId)} />
                  {t.name}
                </label>
              ))}
            </div>
          )}
          {effectiveTeamIds.length >= 2 && (
            <p className="client-subtitle">
              {bracketSize}-team bracket{byeCount > 0 ? ` · ${byeCount} bye${byeCount === 1 ? "" : "s"}` : ""}
            </p>
          )}
          <input className="client-input" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          <input className="client-input" type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
          <input className="client-input" placeholder="Location (optional)" value={location}
            onChange={(e) => setLocation(e.target.value)} />
        </div>
        {error && <div className="client-error">{error}</div>}
        <div className="coach-modal-actions">
          <button className="client-button-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="client-button" onClick={save} disabled={saving || !canSave}>
            {saving ? "Creating…" : "Create Tournament"}
          </button>
        </div>
      </div>
    </div>
  );
}

function TournamentCard({ tournament, onClick }) {
  const statusLabel = tournament.status === "completed" ? "Completed" : tournament.status === "active" ? "In Progress" : "Not Seeded";
  return (
    <button className="coach-history-row league-tournament-card" onClick={onClick}>
      <div>
        <div className="coach-history-matchup"><Trophy size={16} /> {tournament.name || "Untitled Tournament"}</div>
        <div className="coach-history-date">{tournament.teamCount} teams · {statusLabel}</div>
      </div>
    </button>
  );
}

function GameRow({ game, scope, officials, onChanged }) {
  const [expanded, setExpanded] = useState(false);
  const [officialId, setOfficialId] = useState(game.officialId || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [editingStats, setEditingStats] = useState(false);

  // The same three states iOS's LeagueGameDetailScreen branches on. A league
  // owner may edit ANY game in their league since 2026-09-15 — but never one
  // being scored right now: a manual final would stamp the result as theirs,
  // wipe the live fields, and leave the official's own submit to 409.
  const completed = game.status === "completed";
  const liveElsewhere = !completed && game.isLive;

  const assign = async (id) => {
    setSaving(true);
    setError(null);
    try {
      await ApiService.updateLeagueGame(game.gameId, { officialId: id || null });
      onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this game?")) return;
    setSaving(true);
    setError(null);
    try {
      await ApiService.deleteLeagueGame(game.gameId);
      onChanged();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  const assignPicker = (
    <div className="league-assign-row">
      {officials.length === 0 ? (
        <p className="client-subtitle">
          No officials yet — invite one from the Officials tab, then come back to assign them.
        </p>
      ) : (
        <select
          className="client-input"
          value={officialId}
          onChange={(e) => { setOfficialId(e.target.value); assign(e.target.value); }}
          disabled={saving}
        >
          <option value="">Unassigned</option>
          {officials.map((o) => <option key={o.officialId} value={o.officialId}>{o.name || o.email}</option>)}
        </select>
      )}
    </div>
  );

  const scoreText =
    game.scoreA != null && game.scoreB != null
      ? `Final ${Math.round(game.scoreA)} – ${Math.round(game.scoreB)}`
      : "Final";

  return (
    <div className="coach-history-card">
      <button className="coach-history-row" onClick={() => setExpanded((e) => !e)}>
        <div>
          <div className="coach-history-matchup">{game.teamAName} vs {game.teamBName}</div>
          <div className="coach-history-date">{localWhenLine(game)}</div>
          {(game.venue || game.location) && (
            <div className="coach-history-date">{game.venue || game.location}</div>
          )}
          {game.tournamentRoundName && (
            <div className="league-game-tournament-badge">
              <Trophy size={12} /> {game.tournamentName || "Tournament"} · {game.tournamentRoundName}
            </div>
          )}
          {/* Who — and which device — is keeping this right now. Read-only
              here; web has no live tracker. */}
          <LiveScorerLine game={game} />
        </div>
        <div className={`coach-history-score${scope === "upcoming" && !game.officialId && !game.isLive ? " coach-unassigned" : ""}`}>
          {scope === "upcoming"
            ? (game.isLive ? <LiveGameBadge game={game} /> : (game.officialName || "Unassigned"))
            : scoreText}
        </div>
      </button>
      {expanded && (
        <div className="coach-history-detail">
          {completed ? (
            editingStats ? (
              <GameStatsEditor
                matchId={game.gameId}
                teamAName={game.teamAName}
                teamBName={game.teamBName}
                onSaved={() => { setEditingStats(false); onChanged(); }}
                onCancel={() => setEditingStats(false)}
              />
            ) : (
              <>
                {game.officialName && (
                  <p className="client-subtitle">Scorekept by {game.officialName}</p>
                )}
                <button className="client-button" onClick={() => setEditingStats(true)}>Edit stats</button>
              </>
            )
          ) : liveElsewhere ? (
            <>
              <p className="client-subtitle">Stats can be edited once this game is final.</p>
              {assignPicker}
              {error && <div className="client-error">{error}</div>}
            </>
          ) : editingStats ? (
            <GameStatsEditor
              matchId={game.gameId}
              teamAName={game.teamAName}
              teamBName={game.teamBName}
              onSaved={() => { setEditingStats(false); onChanged(); }}
              onCancel={() => setEditingStats(false)}
            />
          ) : (
            <>
              {assignPicker}
              {error && <div className="client-error">{error}</div>}
              {/* Saving stats FINALS the game, so it warns first — same
                  confirmation iOS shows for a game assigned to someone else. */}
              <button
                className="client-button"
                onClick={() => {
                  if (window.confirm("Saving stats marks this game final. Whoever is assigned to it won't be able to scorekeep it afterward without replacing your result.")) {
                    setEditingStats(true);
                  }
                }}
              >
                Enter stats manually
              </button>
              <button className="league-remove-team" onClick={remove} disabled={saving}>Delete Game</button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function LeagueSchedulePage() {
  const navigate = useNavigate();
  const [scope, setScope] = useState("upcoming");
  const [games, setGames] = useState([]);
  const [teams, setTeams] = useState([]);
  const [officials, setOfficials] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddGame, setShowAddGame] = useState(false);
  const [showAddTournament, setShowAddTournament] = useState(false);

  const loadGames = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await ApiService.getLeagueGames(scope);
      setGames(resp.games || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadOfficials = async () => {
    try {
      const resp = await ApiService.getLeagueOfficials();
      setOfficials(resp.officials || []);
    } catch {
      // non-fatal — the assign picker just shows fewer options
    }
  };

  const loadTournaments = async () => {
    try {
      const resp = await ApiService.getLeagueTournaments();
      setTournaments(resp.tournaments || []);
    } catch {
      // non-fatal — the tournaments section just stays hidden
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const resp = await ApiService.getLeagueTeams();
        setTeams(resp.teams || []);
      } catch {
        // add-game / add-tournament modals just show no teams
      }
    })();
    loadOfficials();
    loadTournaments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadGames();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  return (
    <div>
      <div className="coach-page-header">
        <h1 className="client-h1">Schedule</h1>
        <div className="league-schedule-add-actions">
          <button className="client-button-secondary" onClick={() => setShowAddGame(true)}>+ Game</button>
          <button className="client-button" onClick={() => setShowAddTournament(true)}>+ Tournament</button>
        </div>
      </div>

      {tournaments.length > 0 && (
        <div className="coach-history-list league-tournament-list">
          {tournaments.map((t) => (
            <TournamentCard key={t.tournamentId} tournament={t} onClick={() => navigate(`/league/tournaments/${t.tournamentId}`)} />
          ))}
        </div>
      )}

      <div className="coach-scope-toggle">
        <button className={scope === "upcoming" ? "active" : ""} onClick={() => setScope("upcoming")}>Upcoming</button>
        <button className={scope === "history" ? "active" : ""} onClick={() => setScope("history")}>Past</button>
      </div>

      {loading ? (
        <div className="client-loading-row">Loading…</div>
      ) : error ? (
        <div className="client-error">{error}</div>
      ) : games.length === 0 ? (
        <div className="client-empty">{scope === "upcoming" ? "No upcoming games. Schedule one above." : "No games finished yet."}</div>
      ) : (
        <div className="coach-history-list">
          {games.map((g) => (
            <GameRow
              key={g.gameId}
              game={g}
              scope={scope}
              officials={officials}
              onChanged={loadGames}
            />
          ))}
        </div>
      )}

      {showAddGame && (
        <AddGameModal
          teams={teams}
          onClose={() => setShowAddGame(false)}
          onSaved={() => { setShowAddGame(false); loadGames(); }}
        />
      )}

      {showAddTournament && (
        <AddTournamentModal
          teams={teams}
          onClose={() => setShowAddTournament(false)}
          onSaved={(tournamentId) => {
            setShowAddTournament(false);
            loadTournaments();
            navigate(`/league/tournaments/${tournamentId}`);
          }}
        />
      )}
    </div>
  );
}
